import type { ReactNode } from 'react';
import type { Settlement, SettlementLine } from '@domain/policies/SettlementPolicy';
import { Banner } from '@presentation/design-system';
import styles from './SettlementBreakdown.module.css';

/**
 * El desglose de lo que se le debe a alguien al separarse.
 *
 * Una sola pieza para los dos sitios que lo enseñan —la ficha y el modal de
 * baja—, porque un cálculo de nómina pintado en dos lugares acaba divergiendo, y
 * la divergencia se ve como dos cifras distintas para la misma persona.
 *
 * **Cada renglón lleva su artículo y cómo salió.** Quien revisa esto es RR.HH. o
 * quien lleve lo laboral, y no debería tener que abrir el código para auditarlo.
 */

export const pesos = (cents: number): string =>
  (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

/** Acepta «30000», «30,000» y «$30,000.50». Devuelve centavos, o null. */
export const parsePesos = (raw: string): number | null => {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (cleaned.length === 0) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
};

export const SettlementBreakdown = ({
  settlement,
}: {
  readonly settlement: Settlement;
}): ReactNode => (
  <div className={styles.breakdown}>
    {/* El aviso va arriba y no al pie: un cálculo de nómina que se lee sin la
        advertencia se paga sin la advertencia. */}
    <Banner tone="warn">
      Cálculo de referencia según la LFT. No sustituye una revisión legal —
      confírmalo con RR.HH. antes de pagar.
    </Banner>

    <Section
      title="Finiquito"
      note="Se debe siempre, renuncie o la corran."
      lines={settlement.finiquito}
      totalCents={settlement.finiquitoTotalCents}
    />

    {settlement.indemnizacion.length > 0 && (
      <Section
        title="Indemnización"
        note="Sólo por el tipo de separación elegido."
        lines={settlement.indemnizacion}
        totalCents={settlement.indemnizacionTotalCents}
      />
    )}

    <div className={styles.grandTotal}>
      <span>Total</span>
      <span>{pesos(settlement.totalCents)}</span>
    </div>

    <p className={styles.note}>
      Antigüedad {settlement.yearsOfService} años · salario diario{' '}
      {pesos(settlement.dailySalaryCents)} · integrado{' '}
      {pesos(settlement.integratedDailySalaryCents)}
    </p>
  </div>
);

const Section = ({
  title,
  note,
  lines,
  totalCents,
}: {
  readonly title: string;
  readonly note: string;
  readonly lines: readonly SettlementLine[];
  readonly totalCents: number;
}): ReactNode => (
  <section className={styles.section}>
    <h3 className={styles.sectionTitle}>{title}</h3>
    <p className={styles.note}>{note}</p>
    <ul className={styles.lines}>
      {lines.map((line) => (
        <li key={line.concept} className={styles.line}>
          <span className={styles.lineLabel}>
            {line.concept}
            <small>
              {line.legalBasis} · {line.detail}
            </small>
          </span>
          <span className={styles.lineValue}>{pesos(line.amountCents)}</span>
        </li>
      ))}
      <li className={styles.subtotal}>
        <span>Subtotal</span>
        <span>{pesos(totalCents)}</span>
      </li>
    </ul>
  </section>
);
